import { useEffect, useId, useRef } from 'react';
import { cn } from '../../../lib/styles';
import { Icon } from '../index.spark';
import type { CheckboxProps } from './types';

export function Checkbox({
  label,
  description,
  id,
  children,
  error,
  indeterminate,
  ...inputProps
}: CheckboxProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const descriptionId = description ? `${inputId}-description` : undefined;
  // `indeterminate` is a DOM property, not an attribute, so it must be set imperatively.
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-sm text-spark-text-primary">
      <input
        {...inputProps}
        ref={ref}
        id={inputId}
        type="checkbox"
        aria-describedby={descriptionId}
        className={cn(
          'form-checkbox col-start-1 row-start-1 size-4 cursor-pointer rounded border-gray-300 text-spark-purple shadow-xs hover:bg-gray-50 focus:border-spark-purple focus:ring-spark-purple disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-spark-text-secondary',
          error &&
            'border-spark-text-danger bg-red-100 text-spark-text-danger hover:bg-red-200 focus:border-spark-text-danger focus:ring-spark-text-danger disabled:bg-red-200',
        )}
      />

      <label
        htmlFor={inputId}
        className={cn(
          'col-start-2 row-start-1 flex items-center gap-2',
          inputProps.disabled ? 'cursor-not-allowed text-spark-text-secondary' : 'cursor-pointer',
        )}
      >
        <span>{label}</span>
      </label>

      {description && (
        <p id={descriptionId} className="col-start-2 text-spark-text-secondary">
          {description}
        </p>
      )}

      {error && (
        <div className="col-start-2 flex items-center gap-1 text-spark-text-danger">
          <Icon name="xCircle" />
          {error}
        </div>
      )}

      {inputProps.checked && children && <div className="col-start-2 mt-1">{children}</div>}
    </div>
  );
}
